
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';
import Card from '../../components/base/Card';

export default function Features() {
  const features = [
    {
      icon: 'ri-upload-cloud-2-line',
      title: '간편한 엑셀 업로드',
      description: '정해진 양식의 엑셀 파일만 업로드하면 수백 명의 증명서를 한 번에 발급할 수 있습니다.',
      details: [
        '표준 엑셀 템플릿 제공',
        '대용량 파일 지원 (최대 10,000명)',
        '실시간 업로드 진행률 표시',
        '오류 검증 및 자동 수정 제안'
      ]
    },
    {
      icon: 'ri-shield-check-line',
      title: '안전한 증명서 관리',
      description: '고유 인증코드와 암호화된 링크로 증명서의 위변조를 방지하고 안전하게 관리합니다.',
      details: [
        'SHA-256 암호화 인증코드',
        'QR코드 기반 진위 확인',
        '접근 로그 및 감사 추적',
        '만료일 자동 관리'
      ]
    },
    {
      icon: 'ri-smartphone-line',
      title: '언제든 조회 가능',
      description: '학습자는 회원가입 없이도 이메일이나 인증코드로 언제든지 증명서를 조회하고 출력할 수 있습니다.',
      details: [
        '24시간 언제든 접근 가능',
        '모바일 최적화 인터페이스',
        'PDF 다운로드 및 인쇄',
        '소셜 미디어 공유 기능'
      ]
    },
    {
      icon: 'ri-palette-line',
      title: '맞춤형 템플릿',
      description: '기관의 브랜드에 맞는 로고, 직인, 디자인으로 전문적인 증명서를 제작할 수 있습니다.',
      details: [
        '다양한 디자인 템플릿',
        '로고 및 직인 업로드',
        '색상 및 폰트 커스터마이징',
        '미리보기 및 실시간 편집'
      ]
    },
    {
      icon: 'ri-bar-chart-line',
      title: '통계 및 분석',
      description: '발급 현황, 조회 통계, 결제 내역 등을 한눈에 확인할 수 있는 대시보드를 제공합니다.',
      details: [
        '실시간 발급 현황 모니터링',
        '월별/연도별 통계 리포트',
        '학습자 활동 분석',
        '수익 및 정산 관리'
      ]
    },
    {
      icon: 'ri-customer-service-2-line',
      title: '24/7 고객지원',
      description: '전문 고객지원팀이 언제든지 도움을 드리며, 시스템 사용법부터 기술적 문제까지 해결해드립니다.',
      details: [
        '실시간 채팅 지원',
        '전화 및 이메일 상담',
        '원격 화면 공유 지원',
        '사용자 교육 및 가이드'
      ]
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      <Header />
      
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-blue-50 to-indigo-100 py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-6">
            CEIS의 <span className="text-blue-600">강력한 기능들</span>
          </h1>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            교육기관의 증명서 발급 업무를 혁신하는 다양한 기능들을 만나보세요. 
            복잡한 과정 없이 간단하고 안전하게 증명서를 관리할 수 있습니다.
          </p>
        </div>
      </section>

      {/* Features Grid */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            {features.map((feature, index) => (
              <Card key={index} className="hover:shadow-lg transition-shadow">
                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center flex-shrink-0">
                    <i className={`${feature.icon} text-xl text-blue-600`}></i>
                  </div>
                  <div className="flex-1">
                    <h3 className="text-xl font-semibold text-gray-900 mb-3">
                      {feature.title}
                    </h3>
                    <p className="text-gray-600 mb-4">
                      {feature.description}
                    </p>
                    <ul className="space-y-2">
                      {feature.details.map((detail, detailIndex) => (
                        <li key={detailIndex} className="flex items-center text-sm text-gray-600">
                          <i className="ri-check-line text-green-600 mr-2"></i>
                          {detail}
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Process Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-4">
              간단한 증명서 발급 프로세스
            </h2>
            <p className="text-xl text-gray-600">
              복잡한 설정 없이 몇 번의 클릭만으로 전문적인 증명서를 발급하세요.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            {[
              {
                step: '01',
                title: '기관 등록',
                description: '교육기관 정보를 입력하고 승인을 받습니다.',
                icon: 'ri-building-line'
              },
              {
                step: '02',
                title: '템플릿 설정',
                description: '로고와 디자인을 설정하여 맞춤 템플릿을 생성합니다.',
                icon: 'ri-palette-line'
              },
              {
                step: '03',
                title: '엑셀 업로드',
                description: '학습자 정보가 담긴 엑셀 파일을 업로드합니다.',
                icon: 'ri-file-excel-line'
              },
              {
                step: '04',
                title: '증명서 발급',
                description: '자동으로 모든 학습자에게 증명서가 발급됩니다.',
                icon: 'ri-award-line'
              }
            ].map((process, index) => (
              <div key={index} className="text-center">
                <div className="relative mb-6">
                  <div className="w-16 h-16 bg-blue-600 text-white rounded-full flex items-center justify-center mx-auto mb-4">
                    <i className={`${process.icon} text-2xl`}></i>
                  </div>
                  <div className="absolute -top-2 -right-2 w-8 h-8 bg-blue-100 text-blue-600 rounded-full flex items-center justify-center text-sm font-bold">
                    {process.step}
                  </div>
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">
                  {process.title}
                </h3>
                <p className="text-gray-600">
                  {process.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-4">
              CEIS를 선택해야 하는 이유
            </h2>
            <p className="text-xl text-gray-600">
              기존 방식과 비교했을 때 CEIS가 제공하는 명확한 장점들을 확인해보세요.
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            <Card className="text-center">
              <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <i className="ri-time-line text-2xl text-red-600"></i>
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-4">기존 방식</h3>
              <ul className="text-gray-600 space-y-2 text-left">
                <li>• 수작업으로 증명서 작성</li>
                <li>• 개별 발급으로 시간 소모</li>
                <li>• 재발급 요청 처리 부담</li>
                <li>• 위변조 위험성</li>
                <li>• 보관 및 관리의 어려움</li>
              </ul>
            </Card>

            <Card className="text-center border-2 border-blue-600 bg-blue-50">
              <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <i className="ri-rocket-line text-2xl text-blue-600"></i>
              </div>
              <h3 className="text-xl font-semibold text-blue-600 mb-4">CEIS 방식</h3>
              <ul className="text-gray-700 space-y-2 text-left">
                <li>• 엑셀 업로드로 일괄 발급</li>
                <li>• 90% 시간 단축</li>
                <li>• 학습자 셀프 조회 서비스</li>
                <li>• 암호화된 보안 시스템</li>
                <li>• 클라우드 기반 자동 관리</li>
              </ul>
            </Card>

            <Card className="text-center">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <i className="ri-trophy-line text-2xl text-green-600"></i>
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-4">개선 효과</h3>
              <ul className="text-gray-600 space-y-2 text-left">
                <li>• 업무 효율성 10배 향상</li>
                <li>• 운영 비용 70% 절감</li>
                <li>• 고객 만족도 95% 증가</li>
                <li>• 오류율 99% 감소</li>
                <li>• 24/7 서비스 가능</li>
              </ul>
            </Card>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
